import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, Link as LinkIcon, AlertCircle, Trash2 } from 'lucide-react';
import { AssetMetadata } from '@linein/shared';

interface FilePreview {
  id: string;
  file?: File;
  url?: string;
  previewUrl: string;
  name: string;
  type: 'file' | 'url';
}

interface UploadModalProps {
  onClose: () => void;
  onBatchUpload: (files: File[], metadata: Partial<AssetMetadata>) => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ onClose, onBatchUpload }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [previews, setPreviews] = useState<FilePreview[]>([]);
  const [activityId, setActivityId] = useState('');
  const [uploader, setUploader] = useState('系統管理員');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFilePreviews = (files: File[]) => {
    const newPreviews: FilePreview[] = files.map((file) => ({
      id: `file-${Date.now()}-${Math.random()}`,
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
      type: 'file',
    }));
    setPreviews((prev) => [...prev, ...newPreviews]);
  };

  const addUrlPreviews = () => {
    const trimmedUrls = urlInput.trim();
    if (!trimmedUrls) return;

    const urls = trimmedUrls.split('\n').filter(line => line.trim());
    const newPreviews: FilePreview[] = [];

    for (const url of urls) {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) continue;

      // Validate URL
      try {
        new URL(trimmedUrl);
        newPreviews.push({
          id: `url-${Date.now()}-${Math.random()}`,
          url: trimmedUrl,
          previewUrl: trimmedUrl,
          name: trimmedUrl.split('/').pop() || '外部連結',
          type: 'url',
        });
      } catch {
        setError(`無效的網址: ${trimmedUrl.substring(0, 50)}...`);
        return;
      }
    }

    if (newPreviews.length > 0) {
      setPreviews((prev) => [...prev, ...newPreviews]);
      setUrlInput('');
      setError(null);
    }
  };

  const removePreview = (id: string) => {
    setPreviews((prev) => {
      const preview = prev.find((p) => p.id === id);
      if (preview?.previewUrl && preview.type === 'file') {
        URL.revokeObjectURL(preview.previewUrl);
      }
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      addFilePreviews(files);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      addFilePreviews(files);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (previews.length === 0) {
      setError('請至少選擇一個檔案或輸入一個網址');
      return;
    }

    const filesToUpload = previews
      .filter((p) => p.type === 'file' && p.file)
      .map((p) => p.file!);

    if (filesToUpload.length === 0) {
      setError('目前僅支援檔案上傳，網址上傳功能開發中');
      return;
    }

    const metadata: Partial<AssetMetadata> = {
      uploader,
    };
    
    // Only include activityId if it has a value
    if (activityId.trim()) {
      metadata.activityId = activityId;
    }

    // Close modal immediately
    onClose();
    
    // Start batch upload
    onBatchUpload(filesToUpload, metadata);

    // Cleanup
    previews.forEach((p) => {
      if (p.type === 'file' && p.previewUrl) {
        URL.revokeObjectURL(p.previewUrl);
      }
    });
  };

  const handleUrlKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      addUrlPreviews();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-800">上傳素材</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* Upload Area - Two Columns */}
            <div className="grid grid-cols-2 gap-4">
              {/* Left: Drag-Drop Area */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  拖曳或選擇檔案
                </label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all h-[200px] flex flex-col items-center justify-center ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                  }`}
                >
                  <Upload size={40} className="mx-auto text-slate-400 mb-3" />
                  <p className="text-sm font-semibold text-slate-700 mb-1">
                    拖曳檔案或點擊選擇
                  </p>
                  <p className="text-xs text-slate-500">支援圖片、影片</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,video/*"
                  />
                </div>
              </div>

              {/* Right: URL Input */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  或貼上網址（每行一個）
                </label>
                <div className="relative h-[200px]">
                  <textarea
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={handleUrlKeyDown}
                    placeholder={'https://example.com/image1.jpg\nhttps://example.com/image2.jpg\n（Ctrl+Enter 加入）'}
                    className="w-full h-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={addUrlPreviews}
                    className="absolute bottom-2 right-2 px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-md transition-colors shadow-sm"
                  >
                    加入
                  </button>
                </div>
              </div>
            </div>

            {/* Preview Grid */}
            {previews.length > 0 && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  預覽 ({previews.length} 個檔案)
                </label>
                <div className="grid grid-cols-6 gap-2 p-3 bg-slate-50 rounded-xl max-h-48 overflow-y-auto">
                  {previews.map((preview) => (
                    <div
                      key={preview.id}
                      className="relative group aspect-square bg-white rounded-lg overflow-hidden border-2 border-slate-200 hover:border-blue-500 transition-all"
                    >
                      <img
                        src={preview.previewUrl}
                        alt={preview.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23e2e8f0" width="100" height="100"/%3E%3Ctext x="50" y="50" font-size="12" text-anchor="middle" dy=".3em" fill="%2394a3b8"%3E%E7%84%A1%E6%B3%95%E9%A1%AF%E7%A4%BA%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => removePreview(preview.id)}
                          className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-all"
                        >
                          <Trash2 size={12} />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 p-1">
                          <p className="text-white text-[10px] font-medium truncate">
                            {preview.name}
                          </p>
                        </div>
                      </div>
                      {preview.type === 'url' && (
                        <div className="absolute top-1 left-1 p-0.5 bg-blue-500 rounded-full">
                          <LinkIcon size={10} className="text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata Inputs */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  活動編號
                </label>
                <input
                  type="text"
                  value={activityId}
                  onChange={(e) => setActivityId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例: ACT-2024-001"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  上傳者
                </label>
                <input
                  type="text"
                  value={uploader}
                  onChange={(e) => setUploader(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                <AlertCircle size={16} />
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}
          </div>

          {/* Footer - Always Visible */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50 shrink-0">
            <div className="text-sm text-slate-600">
              {previews.length > 0 ? (
                <span>
                  已選擇 <strong className="text-blue-600">{previews.length}</strong> 個檔案
                </span>
              ) : (
                <span>尚未選擇檔案</span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-100 transition-colors"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={previews.length === 0}
                className="px-5 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
              >
                開始上傳 {previews.length > 0 && `(${previews.length})`}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadModal;
